import React, { useEffect, useRef, useState } from "react";
import { Avatar, Box, Typography, Switch, FormControlLabel, TextField, Select, MenuItem, Button, Stack, Alert, CircularProgress } from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";
import { getAuthToken } from "../../../utils/getAuthToken";

const SERVER = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

// Same picture rules the server enforces (JPG/PNG, WhatsApp's 5 MB image limit); the server stays the authority.
const PROFILE_IMAGE_ACCEPT = ".jpg,.jpeg,.png";
const PROFILE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

// The business details shown in the contact's chat page (Business info). The phone number is the connected WhatsApp number, read-only here.
const EMPTY_INFO = { name: "", email: "", website: "", address: "" };
const INFO_FIELDS = [
  ["name", "Business name", 100],
  ["email", "Email", 254],
  ["website", "Website (https://...)", 200],
  ["address", "Address", 300],
];

const UNITS = [
  { key: "minutes", label: "Minutes", inMinutes: 1 },
  { key: "hours", label: "Hours", inMinutes: 60 },
  { key: "days", label: "Days", inMinutes: 1440 },
  { key: "never", label: "Never expires", inMinutes: null },
];

// Shows the stored expiry in the largest unit that divides it exactly (1440 -> 1 day, 90 -> 90 minutes); null = never expires.
const toFields = (minutes) => {
  if (minutes === null) return { amount: "", unit: "never" };
  const unit = [...UNITS].reverse().find((u) => u.inMinutes && minutes % u.inMinutes === 0) || UNITS[0];
  return { amount: String(minutes / unit.inMinutes), unit: unit.key };
};

// The switch and link expiry of the WhatsApp Webchat handoff (docs/WHATSAPP_WEB_HANDOFF_PLAN.md, section 2). The server is the
// authority: it refuses to turn the switch on unless the Meta connection is CONNECTED (`available` says whether that holds) and it
// validates the expiry range. The switch saves at once; the expiry is typed freely and saved with its button.
const WhatsAppWebSettings = ({ workspaceId, onChanged }) => {
  const [settings, setSettings] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState("hours");
  const [imageBusy, setImageBusy] = useState(false);
  const [info, setInfo] = useState(EMPTY_INFO);
  const imageInputRef = useRef(null);

  const url = `${SERVER}/api/workspaces/${workspaceId}/whatsapp-web`;

  const applySettings = (next) => {
    setSettings(next);
    const fields = toFields(next.linkExpiryMinutes);
    setAmount(fields.amount);
    setUnit(fields.unit);
    setInfo({ ...EMPTY_INFO, ...next.businessInfo, phone: undefined });
    if (onChanged) onChanged(next);
  };

  useEffect(() => {
    if (!workspaceId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = await getAuthToken();
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled && res.data?.settings) applySettings(res.data.settings);
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.message || "Could not load WhatsApp Webchat settings.");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, workspaceId]);

  const save = async (change) => {
    setSaving(true);
    try {
      const token = await getAuthToken();
      const res = await axios.put(url, change, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.settings) applySettings(res.data.settings);
      if (change.enabled === false) toast.success("WhatsApp Webchat turned off. Open chats and unused links have ended.");
      if (change.linkExpiryMinutes !== undefined) toast.success("Link expiry saved.");
      if (change.businessInfo !== undefined) toast.success("Business info saved.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save WhatsApp Webchat settings.");
    } finally {
      setSaving(false);
    }
  };

  // The business picture shown in the contact's chat page (header and business info). Uploading replaces it; removing goes back to initials.
  const changeProfileImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // so the same file can be chosen again
    if (!file) return;
    if (file.size === 0 || file.size > PROFILE_IMAGE_MAX_BYTES) {
      toast.error(file.size === 0 ? "That file is empty." : "The picture must be 5 MB or smaller.");
      return;
    }
    setImageBusy(true);
    try {
      const token = await getAuthToken();
      const form = new FormData();
      form.append("file", file, file.name);
      const res = await axios.post(`${url}/profile-image`, form, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.settings) applySettings(res.data.settings);
      toast.success("Profile picture saved.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save the profile picture.");
    } finally {
      setImageBusy(false);
    }
  };

  const removeProfileImage = async () => {
    setImageBusy(true);
    try {
      const token = await getAuthToken();
      const res = await axios.delete(`${url}/profile-image`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.settings) applySettings(res.data.settings);
      toast.success("Profile picture removed.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not remove the profile picture.");
    } finally {
      setImageBusy(false);
    }
  };

  if (loadError) return <Alert severity="error">{loadError}</Alert>
  if (!settings) return <CircularProgress size={20} />;

  const never = unit === "never";
  // null = never expires, which is what the API stores for it.
  const minutes = never ? null : Number(amount) * (UNITS.find((u) => u.key === unit)?.inMinutes || 1);
  const expiryError =
    !never && (amount === "" || !Number.isInteger(minutes) || minutes < settings.minExpiryMinutes || minutes > settings.maxExpiryMinutes)
      ? `Enter a whole number of minutes, hours or days between ${settings.minExpiryMinutes} minutes and ${settings.maxExpiryMinutes / 1440} days.`
      : "";
  const unchanged = minutes === settings.linkExpiryMinutes;
  const infoUnchanged = INFO_FIELDS.every(([key]) => info[key].trim() === (settings.businessInfo?.[key] || ""));

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Let a contact continue the same conversation in a browser chat. A workflow sends them a one-time link inside WhatsApp.
      </Typography>

      {!settings.available && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Available only while the WhatsApp Business Platform (API) is connected.
        </Alert>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={settings.enabled}
            disabled={saving || (!settings.available && !settings.enabled)}
            onChange={(event) => save({ enabled: event.target.checked })}
          />
        }
        label={settings.enabled ? "On" : "Off"}
      />

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Chat profile picture
      </Typography>
      <Stack direction="row" gap={1.5} alignItems="center">
        <Avatar src={settings.profileImageUrl || undefined} sx={{ width: 56, height: 56 }} />
        <input ref={imageInputRef} type="file" accept={PROFILE_IMAGE_ACCEPT} onChange={changeProfileImage} hidden />
        <Button variant="outlined" disabled={imageBusy} onClick={() => imageInputRef.current?.click()} sx={{ textTransform: "none" }}>
          {settings.profileImageUrl ? "Change picture" : "Upload picture"}
        </Button>
        {settings.profileImageUrl && (
          <Button color="inherit" disabled={imageBusy} onClick={removeProfileImage} sx={{ textTransform: "none" }}>
            Remove
          </Button>
        )}
        {imageBusy && <CircularProgress size={18} />}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        Shown next to the business name in the contact's chat page. JPG or PNG, up to 5 MB. Without one, the business initials are shown.
      </Typography>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Business info
      </Typography>
      <Stack gap={1.5} sx={{ maxWidth: 420 }}>
        <TextField size="small" label="Phone number" value={settings.businessInfo?.phone || ""} disabled helperText="The WhatsApp number you connected." />
        {INFO_FIELDS.map(([key, label, max]) => (
          <TextField
            key={key}
            size="small"
            label={label}
            value={info[key]}
            onChange={(event) => setInfo((current) => ({ ...current, [key]: event.target.value }))}
            inputProps={{ maxLength: max }}
            multiline={key === "address"}
          />
        ))}
        <Button
          variant="outlined"
          disabled={saving || infoUnchanged}
          onClick={() => save({ businessInfo: info })}
          sx={{ textTransform: "none", alignSelf: "flex-start" }}
        >
          Save business info
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        Shown to the contact under Business info in their chat page. Leave a field empty to hide it. Without a name, the workspace name is used.
      </Typography>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Link expiry
      </Typography>
      <Stack direction="row" gap={1} alignItems="flex-start">
        <TextField
          size="small"
          type="number"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          inputProps={{ min: 1, step: 1, "aria-label": "Link expiry amount" }}
          error={Boolean(expiryError)}
          disabled={never}
          sx={{ width: 120 }}
        />
        <Select size="small" value={unit} onChange={(event) => setUnit(event.target.value)} inputProps={{ "aria-label": "Link expiry unit" }}>
          {UNITS.map((u) => (
            <MenuItem key={u.key} value={u.key}>
              {u.label}
            </MenuItem>
          ))}
        </Select>
        <Button
          variant="outlined"
          disabled={saving || Boolean(expiryError) || unchanged}
          onClick={() => save({ linkExpiryMinutes: minutes })}
          sx={{ textTransform: "none", height: 40 }}
        >
          Save
        </Button>
      </Stack>
      <Typography variant="caption" color={expiryError ? "error" : "text.secondary"} sx={{ display: "block", mt: 1 }}>
        {expiryError ||
          (never
            ? "Links never expire. Each stays usable (once, in the first browser that opens it) and its chat stays open until you revoke it, a newer link replaces it, or WhatsApp Webchat is turned off."
            : "A link must be opened before it expires, and the chat it opens ends at the same time. Changing this does not affect links already sent.")}
      </Typography>
      {never && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          While a link is active, the contact's other WhatsApp workflows do not run. With no expiry, that lasts until the link is revoked or replaced, even if the contact never opens it.
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        Turning WhatsApp Webchat off ends every open chat and unused link immediately.
      </Typography>
    </Box>
  );
};

export default WhatsAppWebSettings;
